// Pure helpers for the Red Flag Rules page: month arithmetic, labels, draft <-> API
// conversion, rules per role and client-side checks.
// No React here, so every rule the page applies can be unit-tested directly.

import type {
  RedFlagConditionOp,
  RedFlagMetricSpec,
  RedFlagRoleOption,
  RedFlagRoleRules,
  RedFlagRoleRulesInput,
  RedFlagRuleInput,
  RedFlagRuleView,
  RedFlagRulesCatalog,
  RedFlagRulesConfig,
  RedFlagStaleRef,
  RedFlagSystemRuleSpec,
  RedFlagTriggerMode,
} from '../../services/api';

// ── Months ───────────────────────────────────────────────────────────────────

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** 'YYYY-MM' from 'YYYY-MM' or 'YYYY-MM-DD'; '' when unparseable. */
export const monthKeyOf = (raw: string | null | undefined): string => {
  const m = /^(\d{4})-(\d{2})/.exec(String(raw ?? ''));
  return m ? `${m[1]}-${m[2]}` : '';
};

/** 'YYYY-MM' + n months. */
export const addMonths = (monthKey: string, n: number): string => {
  const m = /^(\d{4})-(\d{2})$/.exec(monthKey);
  if (!m) return monthKey;
  const idx = Number(m[1]) * 12 + (Number(m[2]) - 1) + n;
  const y = Math.floor(idx / 12);
  const mo = (idx % 12) + 1;
  return `${y}-${String(mo).padStart(2, '0')}`;
};

/** 'Oct 2026'. Falls back to the raw text so a label never reads "undefined NaN". */
export const monthLabel = (raw: string | null | undefined): string => {
  const key = monthKeyOf(raw);
  if (!key) return String(raw ?? '—');
  const [y, mo] = key.split('-');
  return `${MONTH_NAMES[Number(mo) - 1]} ${y}`;
};

/** A local-clock fallback when the API has not told us the IST month yet. */
export const localMonthKey = (d: Date = new Date()): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

// ── Start date (the day a project's red flags begin) ─────────────────────────

const DAY_KEY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A real calendar day written YYYY-MM-DD. */
export const isDayKey = (raw: string | null | undefined): raw is string => {
  const m = DAY_KEY_RE.exec(String(raw ?? ''));
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  return mo >= 1 && mo <= 12 && d >= 1 && d <= new Date(y, mo, 0).getDate();
};

/** A local-clock fallback when the API has not told us the IST day. */
export const localDayKey = (d: Date = new Date()): string =>
  `${localMonthKey(d)}-${String(d.getDate()).padStart(2, '0')}`;

/** 'YYYY-MM-DD' + n days. */
export const addDays = (dayKey: string, n: number): string => {
  const m = DAY_KEY_RE.exec(dayKey);
  if (!m) return dayKey;
  return localDayKey(new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + n));
};

/** Last day of a 'YYYY-MM' month, 'YYYY-MM-DD'. */
export const monthEndOf = (monthKey: string): string => {
  const m = /^(\d{4})-(\d{2})$/.exec(monthKey);
  if (!m) return monthKey;
  return `${monthKey}-${String(new Date(Number(m[1]), Number(m[2]), 0).getDate()).padStart(2, '0')}`;
};

/** '14 Oct 2026'. Falls back to the raw text. */
export const dayLabel = (raw: string | null | undefined): string => {
  if (!isDayKey(raw)) return String(raw ?? '—');
  const [y, mo, d] = raw.split('-');
  return `${Number(d)} ${MONTH_NAMES[Number(mo) - 1]} ${y}`;
};

/**
 * The days a start date may be: never before today. A project that was never set up
 * can start on any day up to 12 months ahead (the day decides the month the rules are
 * saved for); once a month is fixed, the day stays inside it.
 */
export const startDateBounds = (today: string, fixedMonth: string | null): { min: string; max: string } => {
  if (!fixedMonth) return { min: today, max: monthEndOf(addMonths(monthKeyOf(today), 12)) };
  const first = `${fixedMonth}-01`;
  return { min: first > today ? first : today, max: monthEndOf(fixedMonth) };
};

export interface StartDateChoice {
  key: 'today' | 'tomorrow' | 'next_month';
  label: string;
  day: string;
}

/** The one-tap choices that fall inside the allowed days. */
export const startDateChoices = (today: string, bounds: { min: string; max: string }): StartDateChoice[] => {
  const all: StartDateChoice[] = [
    { key: 'today', label: 'Today', day: today },
    { key: 'tomorrow', label: 'Tomorrow', day: addDays(today, 1) },
    { key: 'next_month', label: '1st of next month', day: `${addMonths(monthKeyOf(today), 1)}-01` },
  ];
  const seen = new Set<string>();
  return all.filter((c) => {
    if (c.day < bounds.min || c.day > bounds.max || seen.has(c.day)) return false;
    seen.add(c.day);
    return true;
  });
};

/** Tomorrow when it is allowed, else the nearest allowed day. */
export const defaultStartDate = (today: string, bounds: { min: string; max: string }): string => {
  const tomorrow = addDays(today, 1);
  if (tomorrow < bounds.min) return bounds.min;
  return tomorrow > bounds.max ? bounds.max : tomorrow;
};

/**
 * Months the "Rules for" picker offers: saved versions before the current month
 * (read-only history), the current month, and up to 12 months ahead.
 */
export const monthOptions = (currentMonth: string, savedMonths: string[]): string[] => {
  const set = new Set<string>();
  for (const s of savedMonths) {
    const k = monthKeyOf(s);
    if (k) set.add(k);
  }
  for (let i = 0; i <= 12; i++) set.add(addMonths(currentMonth, i));
  return [...set].sort();
};

// ── Labels ───────────────────────────────────────────────────────────────────

export const ordinal = (n: number): string => {
  const v = Math.abs(Math.trunc(n));
  const mod100 = v % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  switch (v % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
};

export const TRIGGER_MODE_OPTIONS: Array<{ value: RedFlagTriggerMode; label: string }> = [
  { value: 'immediate', label: 'Immediately, on each occurrence' },
  { value: 'every_nth', label: 'On every Nth occurrence in a calendar month' },
  { value: 'once_at_nth', label: 'Once, on the Nth occurrence in a calendar month' },
];

export const CONDITION_OP_LABELS: Record<RedFlagConditionOp, string> = {
  lt: 'below',
  lte: 'at or below',
  gt: 'above',
  gte: 'at or above',
  eq: 'exactly',
  neq: 'anything but',
};

export const ALL_CONDITION_OPS: RedFlagConditionOp[] = ['lt', 'lte', 'gt', 'gte', 'eq', 'neq'];

export const LEVEL_HINT_LABELS: Record<RedFlagMetricSpec['levelHint'], string> = {
  frontline: 'Store-level',
  manager: 'Managers',
  any: 'Any level',
};

// role_feature_mapping keys → the names Access Management shows for them.
const FEATURE_LABELS: Record<string, string> = {
  emoji: 'Mood check',
  grooming: 'Grooming',
  random_attendance: 'Random attendance',
  training: 'Training',
  attendance: 'Attendance',
  start_end_day: 'Start / End Day',
  tasks: 'Tasks',
  social_feed: 'Social Connect',
  social_post_create: 'Social Connect posts',
  yellow_flag: 'Yellow Flag',
  team_yellow_flag: 'Yellow Flag (Team)',
  store_visits: 'Store Visits',
  sales_targets: 'Sales & Targets',
  team_sales_targets: 'Sales & Targets (Team)',
  grow_content_modules: 'Learning modules',
  grow_sessions: 'Training sessions',
  grow_journeys: 'Learning journeys',
  onboarding_journey: 'Onboarding journey',
  shadow_training_buddy: 'Shadow training',
  onboarding_team: 'Onboarding (Team)',
};

export const featureLabel = (key: string): string =>
  FEATURE_LABELS[key] ?? key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

/**
 * A role "captures" a metric's data when it holds at least one of the metric's
 * feature keys. Returns the human label of what it lacks, or null when covered
 * (or when the metric does not depend on role features).
 */
export const missingCapture = (role: RedFlagRoleOption, metric: RedFlagMetricSpec | undefined): string | null => {
  const keys = metric?.featureKeys ?? [];
  if (keys.length === 0) return null;
  const held = new Set(role.featureKeys ?? []);
  if (keys.some((k) => held.has(k))) return null;
  return keys.map(featureLabel).join(' / ');
};

/** Suppressed (<5) counts of PEOPLE covered arrive as null. */
export const fmtCount = (n: number | null | undefined): string =>
  n === null || n === undefined ? 'fewer than 5' : n.toLocaleString('en-IN');

/**
 * A hidden occurrence / red-flag / employees-flagged count. Never "fewer than 5": the
 * counts are hidden because few PEOPLE are covered (or the parameter is sensitive), and
 * the hidden number itself can be large.
 */
export const fmtHiddenCount = (n: number | null | undefined): string =>
  n === null || n === undefined ? 'Hidden' : n.toLocaleString('en-IN');

/**
 * Were this preview row's counts hidden because it covers fewer than 5 people (so its
 * people count may honestly read "fewer than 5")? An older API only ever hid small
 * groups; a newer one also hides sensitive parameters whatever their size, and says why
 * in the note.
 */
export const hiddenForSmallGroup = (r: { suppressed?: boolean; note?: string }): boolean =>
  !r.suppressed || !r.note || /fewer than \d+ people/i.test(r.note);

/** People covered by a preview row: "fewer than 5" for a small group, "Hidden" otherwise. */
export const fmtPeopleCount = (n: number | null | undefined, r: { suppressed?: boolean; note?: string }): string =>
  n === null || n === undefined ? (hiddenForSmallGroup(r) ? fmtCount(n) : 'Hidden') : n.toLocaleString('en-IN');

export const staleRefText = (ref: RedFlagStaleRef): string => {
  if (typeof ref === 'string') return ref;
  const what = ref.message ?? ref.reason ?? [ref.field, ref.value ?? ref.ref].filter(Boolean).join(' ');
  return [ref.code, what].filter(Boolean).join(': ') || 'A reference that no longer exists';
};

// ── Audience (months set before rules per role) ──────────────────────────────

export interface RuleContext {
  metric?: RedFlagMetricSpec;
  roles: RedFlagRoleOption[];
}

/**
 * Who a rule saved before rules per role applied to, e.g. "store-level employees and
 * the Area Manager role". Shown when reading such a month (its role cards are a
 * projection, and "anyone who manages people" has no exact role-by-role equivalent).
 */
export const audienceText = (rule: RedFlagRuleView, ctx: RuleContext): string => {
  const { roles } = ctx;
  const parts: string[] = [];
  if (rule.appliesToFrontline && ctx.metric?.levelHint !== 'manager') parts.push('store-level employees');
  if (rule.appliesToPeopleManagers) parts.push('anyone who manages people');
  const names = rule.targetRoleIds.map((id) => roles.find((r) => roleKey(r.id) === roleKey(id))?.name ?? 'a removed role');
  if (names.length > 0) parts.push(names.length === 1 ? `the ${names[0]} role` : `the ${names.join(', ')} roles`);
  if (parts.length === 0) return 'nobody';
  if (parts.length === 1) return parts[0];
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
};

// ── Draft <-> API ────────────────────────────────────────────────────────────

/** Only the knobs the metric declares, so stale keys never ride along. */
const cleanParams = (params: Record<string, unknown> | null, metric: RedFlagMetricSpec | undefined): Record<string, unknown> | null => {
  if (!metric || metric.params.length === 0) return params && Object.keys(params).length ? { ...params } : null;
  const out: Record<string, unknown> = {};
  for (const p of metric.params) {
    const v = params?.[p.key];
    if (v !== undefined && v !== null && v !== '') out[p.key] = v;
  }
  return Object.keys(out).length ? out : null;
};

/** The PUT/preview shape of a draft rule, normalised so equal drafts compare equal. */
export const toRuleInput = (rule: RedFlagRuleView, metric?: RedFlagMetricSpec): RedFlagRuleInput => {
  const gated = rule.mode !== 'immediate';
  const isValueMetric = rule.kind === 'custom' && metric?.valueType === 'value';
  return {
    code: rule.code,
    enabled: rule.enabled,
    mode: rule.mode,
    threshold: gated ? Math.trunc(Number(rule.threshold) || 0) : 1,
    joinDayExempt: rule.joinDayExempt,
    appealable: rule.appealable,
    instanceAppealable: rule.appealable && gated ? rule.instanceAppealable : false,
    notifyEmployee: rule.notifyEmployee,
    appliesToFrontline: metric?.levelHint === 'manager' ? false : rule.appliesToFrontline,
    appliesToPeopleManagers: rule.appliesToPeopleManagers,
    targetRoleIds: [...new Set(rule.targetRoleIds)],
    countsTowardExit: rule.countsTowardExit,
    metricParams: rule.kind === 'custom' ? cleanParams(rule.metricParams, metric) : null,
    condition: isValueMetric && rule.condition ? { op: rule.condition.op, value: Number(rule.condition.value) } : null,
    displayOrder: rule.displayOrder,
  };
};

/** Defensive defaults so a sparse/older payload still renders. */
export const normaliseRule = (r: RedFlagRuleView): RedFlagRuleView => ({
  ...r,
  kind: r.kind === 'custom' ? 'custom' : 'system',
  mode: r.mode ?? 'immediate',
  threshold: Number(r.threshold) || 1,
  targetRoleIds: Array.isArray(r.targetRoleIds) ? r.targetRoleIds : [],
  appliesToFrontline: r.appliesToFrontline ?? true,
  appliesToPeopleManagers: r.appliesToPeopleManagers ?? false,
  countsTowardExit: r.countsTowardExit ?? r.kind !== 'custom',
  notifyEmployee: r.notifyEmployee ?? true,
  appealable: r.appealable ?? true,
  instanceAppealable: r.instanceAppealable ?? false,
  joinDayExempt: r.joinDayExempt ?? false,
  metricParams: r.metricParams ?? null,
  condition: r.condition ?? null,
  displayOrder: Number.isFinite(r.displayOrder) ? r.displayOrder : 0,
});

/** A standard rule with its built-in defaults. */
export const systemRuleFromSpec = (s: RedFlagSystemRuleSpec, index: number, enabled: boolean): RedFlagRuleView => ({
  code: s.code,
  kind: 'system',
  metricKey: s.code,
  label: s.label,
  description: s.description,
  category: s.category,
  unitNoun: s.unitNoun,
  enabled,
  mode: s.defaultMode,
  threshold: s.defaultMode === 'immediate' ? 1 : s.defaultThreshold,
  joinDayExempt: s.defaultJoinDayExempt,
  appealable: s.defaultAppealable,
  instanceAppealable: s.defaultInstanceAppealable,
  notifyEmployee: true,
  appliesToFrontline: true,
  appliesToPeopleManagers: false,
  targetRoleIds: [],
  countsTowardExit: true,
  metricParams: null,
  condition: null,
  displayOrder: index,
});

/** A new custom rule on `metric`, with the metric's defaults — OFF until the admin turns it on. */
export const customRuleFromMetric = (
  created: { id: number; code: string; name: string },
  metric: RedFlagMetricSpec,
  displayOrder: number,
): RedFlagRuleView => {
  const params: Record<string, unknown> = {};
  for (const p of metric.params) if (p.default !== null && p.default !== undefined) params[p.key] = p.default;
  const isManager = metric.levelHint === 'manager';
  const mode = metric.defaultTrigger?.mode ?? 'immediate';
  return {
    code: created.code,
    kind: 'custom',
    metricKey: metric.key,
    label: created.name,
    description: metric.description,
    category: metric.category,
    unitNoun: metric.unitNoun,
    enabled: false,
    mode,
    threshold: mode === 'immediate' ? 1 : Math.max(1, metric.defaultTrigger?.threshold ?? 1),
    joinDayExempt: metric.defaultJoinDayExempt,
    appealable: metric.defaultAppealable ?? true,
    instanceAppealable: false,
    notifyEmployee: metric.defaultNotifyEmployee ?? true,
    appliesToFrontline: !isManager,
    appliesToPeopleManagers: isManager,
    targetRoleIds: [],
    countsTowardExit: metric.defaultCountsTowardExit ?? false,
    metricParams: Object.keys(params).length ? params : null,
    condition: metric.valueType === 'value'
      ? (metric.defaultCondition ?? { op: metric.conditionOps?.[0] ?? 'lt', value: 0 })
      : null,
    displayOrder,
    customRuleId: created.id,
    archived: false,
  };
};

export interface DraftSource {
  rules: RedFlagRuleView[];
  customRules: Array<{ id: number; code: string; name: string; metricKey: string; archived: boolean }>;
}

/**
 * The editable draft for a month: every standard rule (missing ones added
 * disabled — a saved set that omits a standard code has it OFF), the set's
 * custom rules, and every other non-archived custom rule (off, with its metric's
 * defaults) so an admin can turn it on. Archived rules are left out.
 */
export const buildDraft = (src: DraftSource, catalog: RedFlagRulesCatalog): RedFlagRuleView[] => {
  const byCode = new Map(src.rules.map((r) => [r.code, normaliseRule(r)]));
  const out: RedFlagRuleView[] = [];
  catalog.systemRules.forEach((s, i) => {
    const existing = byCode.get(s.code);
    out.push(existing ? { ...existing, kind: 'system' } : systemRuleFromSpec(s, i, false));
  });
  const nextOrder = () => Math.max(99, ...out.map((r) => r.displayOrder)) + 1;
  const customs = src.rules
    .map(normaliseRule)
    .filter((r) => r.kind === 'custom' && !r.archived)
    .sort((a, b) => a.displayOrder - b.displayOrder);
  const seen = new Set<string>();
  for (const r of customs) {
    const summary = src.customRules.find((c) => c.code === r.code);
    if (summary?.archived) continue;
    seen.add(r.code);
    out.push({ ...r, label: summary?.name ?? r.label, customRuleId: r.customRuleId ?? summary?.id });
  }
  for (const c of src.customRules) {
    if (c.archived || seen.has(c.code)) continue;
    const metric = catalog.metrics.find((m) => m.key === c.metricKey);
    if (!metric) continue;
    out.push(customRuleFromMetric(c, metric, nextOrder()));
  }
  return out;
};

export const metricFor = (rule: RedFlagRuleView, catalog: RedFlagRulesCatalog | null): RedFlagMetricSpec | undefined =>
  rule.kind === 'custom' ? catalog?.metrics.find((m) => m.key === rule.metricKey) : undefined;

/**
 * The PUT / preview rules of a draft. `loadedCodes` = the codes of the loaded month's set:
 * a switched-off custom parameter that is NOT in it (the draft offers every custom
 * parameter so it can be turned on) is left out — a custom code missing from a saved set
 * already means off — so the payload never grows with the project's custom-parameter list
 * and cannot overflow the server's per-set limit.
 */
export const draftInputs = (
  rules: RedFlagRuleView[],
  catalog: RedFlagRulesCatalog | null,
  loadedCodes?: ReadonlySet<string> | null,
): RedFlagRuleInput[] =>
  rules
    .filter((r) => !loadedCodes || r.kind !== 'custom' || r.enabled || loadedCodes.has(r.code))
    .map((r) => toRuleInput(r, metricFor(r, catalog)));

/** Stable fingerprint for dirty checks. */
export const draftFingerprint = (
  rules: RedFlagRuleView[],
  catalog: RedFlagRulesCatalog | null,
  loadedCodes?: ReadonlySet<string> | null,
): string =>
  JSON.stringify(draftInputs(rules, catalog, loadedCodes));

/** Set-level limits the server enforces (catalog.limits), checked before a save. */
export const draftLimitErrors = (inputs: RedFlagRuleInput[], rules: RedFlagRuleView[], catalog: RedFlagRulesCatalog | null): string[] => {
  const limits = catalog?.limits;
  if (!limits) return [];
  const errs: string[] = [];
  if (inputs.length > limits.maxRules) {
    errs.push(`A rule set can hold at most ${limits.maxRules} parameters — archive custom parameters you no longer use.`);
  }
  const kindOf = new Map(rules.map((r) => [r.code, r.kind]));
  const enabledCustom = inputs.filter((r) => r.enabled && kindOf.get(r.code) === 'custom').length;
  if (enabledCustom > limits.maxEnabledCustomRules) {
    errs.push(`At most ${limits.maxEnabledCustomRules} custom parameters can be on at once — turn some off.`);
  }
  return errs;
};

// ── Validation ───────────────────────────────────────────────────────────────

export const THRESHOLD_MIN = 1;
export const THRESHOLD_MAX = 100;

/** The N check every counted (every / once-at-Nth) setting shares; null when fine. */
export const thresholdError = (threshold: number): string | null => {
  const n = Number(threshold);
  return Number.isInteger(n) && n >= THRESHOLD_MIN && n <= THRESHOLD_MAX
    ? null
    : `N must be a whole number from ${THRESHOLD_MIN} to ${THRESHOLD_MAX}.`;
};

/**
 * Problems in a parameter's SHARED settings that would make the server reject the
 * draft. A malformed comparison value is checked on every rule (the set is saved
 * whole); availability and required choices only when it is on for some role
 * (`rule.enabled`), as the server does. Each role's N is checked by roleLineErrors.
 */
export const ruleErrors = (rule: RedFlagRuleView, catalog: RedFlagRulesCatalog | null): string[] => {
  const errs: string[] = [];
  const metric = metricFor(rule, catalog);
  if (rule.kind === 'custom' && metric?.valueType === 'value' && rule.condition && !Number.isFinite(Number(rule.condition.value))) {
    errs.push('Enter the value the reading is compared with.');
  }
  if (!rule.enabled) return errs;
  if (rule.kind === 'custom') {
    if (!metric) {
      errs.push('This parameter’s data source is no longer offered. Turn it off.');
    } else {
      if (!metric.availability.available) errs.push(metric.availability.reason || 'This data is not captured for the project yet.');
      if (metric.valueType === 'value') {
        const c = rule.condition;
        if (!c) errs.push('Enter the value the reading is compared with.');
        else if (metric.conditionOps?.length && !metric.conditionOps.includes(c.op)) errs.push('Pick one of the offered comparisons.');
      }
      for (const p of metric.params) {
        const v = rule.metricParams?.[p.key] ?? p.default;
        if (p.required && (v === null || v === undefined || v === '')) errs.push(`Choose “${p.label}”.`);
        if (p.type === 'number' && v !== null && v !== undefined && v !== '') {
          const n = Number(v);
          if (!Number.isFinite(n)) errs.push(`“${p.label}” must be a number.`);
          else if ((p.min != null && n < p.min) || (p.max != null && n > p.max)) {
            errs.push(`“${p.label}” must be between ${p.min ?? '−∞'} and ${p.max ?? '∞'}.`);
          }
        }
      }
    }
  }
  return errs;
};

export const CUSTOM_NAME_MIN = 2;
export const CUSTOM_NAME_MAX = 120;

export const customNameError = (name: string): string | null => {
  const t = name.trim();
  if (t.length < CUSTOM_NAME_MIN) return `Use at least ${CUSTOM_NAME_MIN} characters.`;
  if (t.length > CUSTOM_NAME_MAX) return `Use at most ${CUSTOM_NAME_MAX} characters.`;
  return null;
};

/** Reads the API's error text in either envelope, with a fallback. */
export const apiErrorMessage = (e: unknown, fallback: string): string => {
  const data = (e as { response?: { data?: { message?: unknown; error?: unknown } } })?.response?.data;
  const msg = data?.message ?? data?.error;
  return typeof msg === 'string' && msg.trim() ? msg : fallback;
};

export const httpStatus = (e: unknown): number | undefined =>
  (e as { response?: { status?: number } })?.response?.status;

/**
 * A 409 from PUT /config that means "someone else saved this month's rules" — the only
 * 409 whose body carries `currentVersion`. Other 409s (a save already in progress, a
 * custom parameter archived meanwhile, …) are plain errors: the draft is still good.
 */
export const isVersionConflict = (e: unknown): boolean => {
  if (httpStatus(e) !== 409) return false;
  const data = (e as { response?: { data?: unknown } })?.response?.data;
  return typeof data === 'object' && data !== null && 'currentVersion' in data;
};

/**
 * A refusal because the caller's identity could not be verified (not a missing
 * capability): the session itself is not accepted (401), or the access check says so.
 */
export const isUnverifiedRefusal = (e: unknown): boolean => {
  const status = httpStatus(e);
  if (status === 401) return true;
  return status === 403 && /verify your identity/i.test(apiErrorMessage(e, ''));
};

// ── Change summary (Save dialog) ─────────────────────────────────────────────

const modeText = (mode: RedFlagTriggerMode): string => {
  if (mode === 'immediate') return 'every time';
  if (mode === 'every_nth') return 'every Nth';
  return 'once at the Nth';
};

/**
 * One line per parameter whose SHARED settings (the ones every role follows) differ
 * between the loaded rules and the draft, e.g. "Late sign-in · every role: every time →
 * every Nth". Each role's on/off and N are summarised by summariseRoleChanges.
 */
export const summariseSharedChanges = (
  baseline: RedFlagRuleView[],
  draft: RedFlagRuleView[],
  catalog: RedFlagRulesCatalog | null,
): string[] => {
  const before = new Map(baseline.map((r) => [r.code, toRuleInput(r, metricFor(r, catalog))]));
  const lines: string[] = [];
  for (const rule of draft) {
    const next = toRuleInput(rule, metricFor(rule, catalog));
    const prev = before.get(rule.code);
    const name = rule.label;
    if (!prev) {
      lines.push(`${name}: added`);
      continue;
    }
    const parts: string[] = [];
    if (prev.mode !== next.mode) parts.push(`${modeText(prev.mode)} → ${modeText(next.mode)}`);
    if (JSON.stringify(prev.condition) !== JSON.stringify(next.condition)) parts.push('what counts');
    if (JSON.stringify(prev.metricParams) !== JSON.stringify(next.metricParams)) parts.push('options');
    if (
      prev.joinDayExempt !== next.joinDayExempt || prev.appealable !== next.appealable
      || prev.instanceAppealable !== next.instanceAppealable || prev.notifyEmployee !== next.notifyEmployee
      || prev.countsTowardExit !== next.countsTowardExit
    ) parts.push('advanced settings');
    if (parts.length === 0 && prev.displayOrder !== next.displayOrder) parts.push('order changed');
    if (parts.length) lines.push(`${name} · every role: ${parts.join(', ')}`);
  }
  return lines;
};

// ── Save dialog copy ─────────────────────────────────────────────────────────

export const NOTE_MAX = 500;

/** "Your change applies Oct 2026–Nov 2026; the Dec 2026 version keeps its own settings." */
export const applyRangeText = (targetMonth: string, laterMonths: string[], startsOn?: string | null): string => {
  const later = laterMonths.map(monthKeyOf).filter((m) => m > targetMonth).sort();
  // A start date after the 1st: say the day, not the month.
  const from = isDayKey(startsOn) && startsOn > `${targetMonth}-01` ? dayLabel(startsOn) : null;
  if (later.length === 0) return `Your change applies from ${from ?? monthLabel(targetMonth)} onward.`;
  const last = addMonths(later[0], -1);
  const range = from
    ? `from ${from} to the end of ${monthLabel(last)}`
    : last === targetMonth ? `${monthLabel(targetMonth)} only` : `${monthLabel(targetMonth)}–${monthLabel(last)}`;
  return `Your change applies ${range}; the ${monthLabel(later[0])} version keeps its own settings.`;
};

/**
 * A read-only month as it is: the six standard rules (missing ones shown off) and
 * the custom rules in that month's set, archived ones included — nothing added.
 */
export const buildReadOnlyView = (src: DraftSource, catalog: RedFlagRulesCatalog): RedFlagRuleView[] => {
  const byCode = new Map(src.rules.map((r) => [r.code, normaliseRule(r)]));
  const out: RedFlagRuleView[] = catalog.systemRules.map((s, i) => {
    const existing = byCode.get(s.code);
    return existing ? { ...existing, kind: 'system' } : systemRuleFromSpec(s, i, false);
  });
  const systemCodes = new Set(catalog.systemRules.map((s) => s.code));
  src.rules
    .map(normaliseRule)
    .filter((r) => !systemCodes.has(r.code))
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .forEach((r) => {
      const summary = src.customRules.find((c) => c.code === r.code);
      out.push({
        ...r,
        kind: 'custom',
        label: summary?.name ?? r.label,
        archived: r.archived ?? summary?.archived ?? false,
        customRuleId: r.customRuleId ?? summary?.id,
      });
    });
  return out;
};

/** The six standard rules with their built-in defaults, switched on; custom rules untouched. */
export const withStandardSix = (draft: RedFlagRuleView[], catalog: RedFlagRulesCatalog): RedFlagRuleView[] => {
  const systemCodes = new Set(catalog.systemRules.map((s) => s.code));
  return [
    ...catalog.systemRules.map((s, i) => systemRuleFromSpec(s, i, true)),
    ...draft.filter((r) => !systemCodes.has(r.code)),
  ];
};

// ── Rules per role ───────────────────────────────────────────────────────────
//
// The admin picks a role, then sets that role's parameters: each role has its own
// on/off and N; everything else about a parameter (when it counts, what counts, the
// joining-day waiver, appeals, notification, Exit Reviews) is ONE setting every role
// shares. People on the roster with no role in the project follow the "No role" line.
// Who follows which role (a person's highest-level role governs) is the server's call;
// the page only edits the lines.

/** The "No role" line's id: people on the roster who hold no role in this project. */
export const NO_ROLE_ID = '00000000-0000-0000-0000-000000000000';

export const NO_ROLE_HINT = 'People on the roster who hold no role in this project.';

/** More role cards than this get a search box and a more compact layout. */
export const ROLE_SEARCH_AFTER = 8;

/** One role's setting of one parameter. */
export interface RoleLine {
  enabled: boolean;
  threshold: number;
}

/** role id (lower-case) → parameter code → that role's line. */
export type RoleRulesDraft = Record<string, Record<string, RoleLine>>;

export interface RoleCardInfo {
  /** Lower-case role id, or NO_ROLE_ID. */
  id: string;
  name: string;
  /** 'none' = the "No role" line; 'removed' = a role the loaded month stores that no longer exists. */
  kind: 'role' | 'none' | 'removed';
  /** 1 = store level, 2, 3 …; null for "No role" and removed roles. */
  level: number | null;
  /** People who follow this card's rules; null = not known right now. */
  people: number | null;
  /** Real roles only: what data the role captures (its feature keys). */
  role?: RedFlagRoleOption;
}

export const roleKey = (id: string | null | undefined): string => String(id ?? '').trim().toLowerCase();

/** The server's rule: "L1" = 1, "L3" = 3; a blank or odd category = 2. */
export const levelOfCategory = (category: unknown): number => {
  const m = /^\s*L\s*(\d+)\s*$/i.exec(String(category ?? ''));
  const n = m ? Number(m[1]) : NaN;
  return Number.isFinite(n) && n >= 1 ? n : 2;
};

export const levelOfRole = (role: RedFlagRoleOption): number =>
  typeof role.level === 'number' && Number.isInteger(role.level) && role.level >= 1 ? role.level : levelOfCategory(role.category);

/** "Store level" / "Level 2"; '' for "No role" (its card explains itself on hover). */
export const levelText = (card: RoleCardInfo): string => {
  if (card.kind === 'removed') return 'No longer in this project';
  if (card.level === null) return '';
  return card.level === 1 ? 'Store level' : `Level ${card.level}`;
};

/** "118 people", "1 person", "— people" when not known. */
export const peopleText = (n: number | null | undefined): string =>
  n === null || n === undefined ? '— people' : `${n.toLocaleString('en-IN')} ${n === 1 ? 'person' : 'people'}`;

/** Held to the store-level limits: a store-level role, or "No role". */
export const isStoreLevelCard = (card: RoleCardInfo): boolean => card.kind === 'none' || card.level === 1;

/** The name on the card of a role the month lists that the catalog does not know yet. */
export const NEW_ROLE_NAME = 'New role';

/**
 * Is this roleRules entry a role that no longer exists? The server says so with
 * `removed: true` (and `false` means a live role); an older server says nothing, and
 * then a role the catalog does not list is taken as removed.
 */
export const isRemovedRoleEntry = (rr: RedFlagRoleRules, catalogIds: ReadonlySet<string>): boolean => {
  if (typeof rr.removed === 'boolean') return rr.removed;
  return !catalogIds.has(roleKey(rr.roleId));
};

/**
 * Is the catalog (fetched once, when the page opened) behind the month just loaded? The
 * month's roleRules lists every role the project has now: a role it names that the
 * catalog lacks was added since (its card would read "Removed role" and its people's
 * lines would be dropped from the save), and a catalog role it does not name was deleted
 * since. Either way the catalog is fetched again before the month's cards are built.
 */
export const catalogIsBehind = (catalog: RedFlagRulesCatalog, roleRules?: RedFlagRoleRules[] | null): boolean => {
  if (!Array.isArray(roleRules)) return false;
  const catalogIds = new Set(catalog.roles.map((r) => roleKey(r.id)));
  const listed = new Set(roleRules.map((rr) => roleKey(rr.roleId)));
  const added = roleRules.some((rr) => {
    const id = roleKey(rr.roleId);
    // A removed role is not in the catalog by definition. An older server cannot say
    // which entries are removed, so any role the catalog lacks asks for a fresh one.
    return id !== '' && id !== NO_ROLE_ID && !catalogIds.has(id) && rr.removed !== true;
  });
  return added || [...catalogIds].some((id) => !listed.has(id));
};

/**
 * The role cards: the project's roles by level then name, then "No role", then any role
 * the loaded month stores that no longer exists (shown so it can be read; never saved).
 *
 * The month's roleRules (when given) lists every role the project has now, so a catalog
 * role it leaves out was deleted since the catalog was read: it gets no card, and so is
 * never sent as a live role. A role it lists that the catalog does not know is a removed
 * role only by isRemovedRoleEntry; one the server calls live (added since, and missed even
 * by a fresh catalog) keeps a live card with its lines, so saving never switches its
 * people's red flags off; the server checks the role on save.
 */
export const buildRoleCards = (catalog: RedFlagRulesCatalog, roleRules?: RedFlagRoleRules[] | null): RoleCardInfo[] => {
  const listed = Array.isArray(roleRules) ? new Set(roleRules.map((rr) => roleKey(rr.roleId))) : null;
  const roles = catalog.roles
    .filter((r) => !listed || listed.has(roleKey(r.id)))
    .map((r): RoleCardInfo => ({
      id: roleKey(r.id),
      name: r.name,
      kind: 'role',
      level: levelOfRole(r),
      people: typeof r.followerCount === 'number' ? r.followerCount : null,
      role: r,
    }))
    .sort((a, b) => (a.level ?? 0) - (b.level ?? 0) || a.name.localeCompare(b.name));
  const catalogIds = new Set(catalog.roles.map((r) => roleKey(r.id)));
  const known = new Set([...roles.map((r) => r.id), NO_ROLE_ID]);
  const unknown: RoleCardInfo[] = [];
  const removed: RoleCardInfo[] = [];
  for (const rr of roleRules ?? []) {
    const id = roleKey(rr.roleId);
    if (!id || known.has(id)) continue;
    known.add(id);
    if (isRemovedRoleEntry(rr, catalogIds)) {
      removed.push({ id, name: 'Removed role', kind: 'removed', level: null, people: null });
    } else {
      unknown.push({ id, name: NEW_ROLE_NAME, kind: 'role', level: null, people: null });
    }
  }
  const noRole: RoleCardInfo = {
    id: NO_ROLE_ID,
    name: 'No role',
    kind: 'none',
    level: null,
    people: typeof catalog.noRoleCount === 'number' ? catalog.noRoleCount : null,
  };
  return [...roles, ...unknown, noRole, ...removed];
};

/** The cards whose lines are saved: every card but removed roles. */
export const liveCards = (cards: RoleCardInfo[]): RoleCardInfo[] => cards.filter((c) => c.kind !== 'removed');

/**
 * Who rules set before rules per role cover, role by role: the server's projection,
 * repeated here only for a payload without roleRules. A role has a parameter on when
 * the parameter is on and covers store-level employees (a level-1 role), names the
 * role, or covers people managers (a role above level 1); "No role" follows store-level
 * employees.
 */
export const projectRoleRules = (rules: RedFlagRuleView[], cards: RoleCardInfo[]): RoleRulesDraft => {
  const out: RoleRulesDraft = {};
  for (const card of liveCards(cards)) {
    const lines: Record<string, RoleLine> = {};
    for (const r of rules) {
      const covered = card.kind === 'none'
        ? r.appliesToFrontline
        : (r.appliesToFrontline && card.level === 1)
          || r.targetRoleIds.some((id) => roleKey(id) === card.id)
          || (r.appliesToPeopleManagers && (card.level ?? 0) > 1);
      lines[r.code] = { enabled: r.enabled && covered, threshold: Number(r.threshold) || 1 };
    }
    out[card.id] = lines;
  }
  return out;
};

/**
 * The lines a month opens with: the server's roleRules (stored, or projected for a month
 * set before rules per role), one line per card and per parameter in `draft`. A parameter
 * a role has no line for is off at the parameter's N.
 */
export const buildRoleDraft = (
  cfg: Pick<RedFlagRulesConfig, 'rules' | 'roleRules'>,
  cards: RoleCardInfo[],
  draft: RedFlagRuleView[],
): RoleRulesDraft => {
  let source: RoleRulesDraft = {};
  if (Array.isArray(cfg.roleRules)) {
    for (const rr of cfg.roleRules) {
      const id = roleKey(rr.roleId);
      const lines = { ...(source[id] ?? {}) };
      for (const l of rr.rules ?? []) lines[l.code] = { enabled: l.enabled === true, threshold: Number(l.threshold) };
      source[id] = lines;
    }
  } else {
    source = projectRoleRules((cfg.rules ?? []).map(normaliseRule), cards);
  }
  const out: RoleRulesDraft = {};
  for (const card of cards) {
    const lines: Record<string, RoleLine> = {};
    for (const r of draft) {
      const given = source[card.id]?.[r.code];
      const n = Number(given?.threshold);
      lines[r.code] = { enabled: given?.enabled === true, threshold: Number.isFinite(n) && n >= 1 ? n : Number(r.threshold) || 1 };
    }
    out[card.id] = lines;
  }
  return out;
};

/** The month's role lines are a projection of rules set before rules per role. */
export const isProjectedMonth = (cfg: Pick<RedFlagRulesConfig, 'source' | 'roleRules' | 'roleRulesProjected'>): boolean => {
  if (cfg.source !== 'saved' && cfg.source !== 'default') return false;
  return Array.isArray(cfg.roleRules) ? cfg.roleRulesProjected === true : true;
};

/** `lines` with one role's line for one parameter replaced. */
export const withLine = (lines: RoleRulesDraft, roleId: string, code: string, line: RoleLine): RoleRulesDraft => ({
  ...lines,
  [roleId]: { ...(lines[roleId] ?? {}), [code]: line },
});

/** Every card gets an off line for a new parameter. */
export const addCodeToLines = (lines: RoleRulesDraft, cards: RoleCardInfo[], code: string, threshold: number): RoleRulesDraft => {
  const out: RoleRulesDraft = { ...lines };
  for (const c of cards) out[c.id] = { ...(lines[c.id] ?? {}), [code]: { enabled: false, threshold } };
  return out;
};

/** Every card loses its line for an archived parameter. */
export const dropCodeFromLines = (lines: RoleRulesDraft, code: string): RoleRulesDraft => {
  const out: RoleRulesDraft = {};
  for (const [id, byCode] of Object.entries(lines)) {
    const rest = { ...byCode };
    delete rest[code];
    out[id] = rest;
  }
  return out;
};

/** Parameters on for a role. */
export const roleOnCount = (lines: RoleRulesDraft, roleId: string, rules: RedFlagRuleView[]): number =>
  rules.filter((r) => lines[roleId]?.[r.code]?.enabled === true).length;

/** On for at least one saved card. */
export const onForSomeRole = (lines: RoleRulesDraft, cards: RoleCardInfo[], code: string): boolean =>
  liveCards(cards).some((c) => lines[c.id]?.[code]?.enabled === true);

/** The N a parameter falls back to when it starts counting (its built-in or metric default). */
export const defaultThresholdOf = (rule: RedFlagRuleView, catalog: RedFlagRulesCatalog | null): number => {
  if (rule.kind === 'system') return catalog?.systemRules.find((s) => s.code === rule.code)?.defaultThreshold ?? 2;
  return metricFor(rule, catalog)?.defaultTrigger?.threshold ?? 2;
};

/**
 * A parameter's shared counting moved from "every time" to every / once-at-Nth: roles
 * still at N = 1 (what "every time" stores) start at `seed` instead.
 */
export const reseedThresholds = (lines: RoleRulesDraft, code: string, seed: number): RoleRulesDraft => {
  const out: RoleRulesDraft = {};
  for (const [id, byCode] of Object.entries(lines)) {
    const line = byCode[code];
    out[id] = line && !(Number(line.threshold) >= 2) ? { ...byCode, [code]: { ...line, threshold: seed } } : byCode;
  }
  return out;
};

/**
 * "Start from the standard six" for a project that was never set up: the six standard
 * parameters on at their built-in N for every store-level role and for "No role", off
 * for higher roles — who the built-in rules have always covered. Custom parameters stay.
 */
export const seedStandardSix = (lines: RoleRulesDraft, cards: RoleCardInfo[], catalog: RedFlagRulesCatalog): RoleRulesDraft => {
  const out: RoleRulesDraft = { ...lines };
  for (const card of liveCards(cards)) {
    const next = { ...(lines[card.id] ?? {}) };
    for (const s of catalog.systemRules) {
      next[s.code] = { enabled: isStoreLevelCard(card), threshold: s.defaultMode === 'immediate' ? 1 : s.defaultThreshold };
    }
    out[card.id] = next;
  }
  return out;
};

export const MANAGER_ONLY_REASON =
  'This parameter describes managers, so it can’t be on for store-level roles or people with no role.';
export const CUSTOM_RULES_OFF_REASON = 'Custom parameters are switched off on this server.';

/** A manager-level custom parameter, on a store-level role or "No role": the server refuses it on. */
export const managerOnlyFor = (rule: RedFlagRuleView, card: RoleCardInfo, catalog: RedFlagRulesCatalog | null): boolean =>
  rule.kind === 'custom' && metricFor(rule, catalog)?.levelHint === 'manager' && isStoreLevelCard(card);

/** Why this parameter cannot be switched on for this role, or null when it can. */
export const cannotTurnOnReason = (
  rule: RedFlagRuleView,
  card: RoleCardInfo,
  catalog: RedFlagRulesCatalog | null,
): string | null => {
  if (card.kind === 'removed') return 'This role no longer exists in the project.';
  if (rule.kind !== 'custom') return null;
  const metric = metricFor(rule, catalog);
  if (!metric) return 'The data this parameter was built on is no longer offered.';
  if (!metric.availability.available) return metric.availability.reason || 'This data is not captured for the project yet.';
  if (catalog?.customRulesEnabled === false) return CUSTOM_RULES_OFF_REASON;
  if (managerOnlyFor(rule, card, catalog)) return MANAGER_ONLY_REASON;
  return null;
};

export interface CopyResult {
  lines: RoleRulesDraft;
  /** Parameters the target could not take on (their names); the target keeps its own line. */
  skipped: string[];
}

/**
 * "Copy from another role": the target role takes the source role's on/off and N for
 * every parameter — nothing else changes. A parameter the target cannot have on (a
 * managers-only parameter on a store-level role, …) keeps the target's line and is reported.
 */
export const copyRoleLines = (
  lines: RoleRulesDraft,
  fromId: string,
  to: RoleCardInfo,
  rules: RedFlagRuleView[],
  catalog: RedFlagRulesCatalog | null,
): CopyResult => {
  const next = { ...(lines[to.id] ?? {}) };
  const skipped: string[] = [];
  for (const r of rules) {
    const src = lines[fromId]?.[r.code];
    if (!src) continue;
    if (src.enabled && !next[r.code]?.enabled && cannotTurnOnReason(r, to, catalog)) {
      skipped.push(r.label);
      continue;
    }
    next[r.code] = { ...src };
  }
  return { lines: { ...lines, [to.id]: next }, skipped };
};

export const lineErrorKey = (roleId: string, code: string): string => `${roleId}|${code}`;

/**
 * Problems in the roles' lines the server would refuse, keyed by lineErrorKey: an N
 * outside 1..100 on a counted parameter that is on, and a managers-only parameter on for
 * a store-level role or "No role".
 */
export const roleLineErrors = (
  rules: RedFlagRuleView[],
  lines: RoleRulesDraft,
  cards: RoleCardInfo[],
  catalog: RedFlagRulesCatalog | null,
): Map<string, string[]> => {
  const out = new Map<string, string[]>();
  for (const card of liveCards(cards)) {
    for (const r of rules) {
      const line = lines[card.id]?.[r.code];
      if (!line?.enabled) continue;
      const errs: string[] = [];
      const nError = r.mode === 'immediate' ? null : thresholdError(line.threshold);
      if (nError) errs.push(nError);
      if (managerOnlyFor(r, card, catalog)) errs.push('Managers only: switch it off for this role.');
      if (errs.length) out.set(lineErrorKey(card.id, r.code), errs);
    }
  }
  return out;
};

export interface PerRoleInputs {
  rules: RedFlagRuleInput[];
  roles: RedFlagRoleRulesInput[];
}

/**
 * The PUT / preview body of a draft saved role by role. `rules` carries each parameter's
 * shared settings; its on/off and N are derived as the server derives them (on = on for
 * some role; N = the smallest N among the roles that have it on) and its audience is
 * empty, as a per-role set stores it. `roles` lists every saved card, "No role" included,
 * with the parameters on for it: a parameter left out is off for that role, so an off
 * line's N is not sent and the body stays small however many roles there are. Should the
 * project have more roles than one save may list, roles with nothing on are left out (the
 * same meaning: everything off). Removed roles are never sent.
 */
export const perRoleInputs = (
  draft: RedFlagRuleView[],
  lines: RoleRulesDraft,
  cards: RoleCardInfo[],
  catalog: RedFlagRulesCatalog | null,
  loadedCodes?: ReadonlySet<string> | null,
): PerRoleInputs => {
  const live = liveCards(cards);
  const derived = draft.map((r): RedFlagRuleView => {
    const ns = live
      .filter((c) => lines[c.id]?.[r.code]?.enabled === true)
      .map((c) => Math.trunc(Number(lines[c.id][r.code].threshold)));
    const finite = ns.filter((n) => Number.isFinite(n));
    return {
      ...r,
      enabled: ns.length > 0,
      threshold: r.mode !== 'immediate' && finite.length > 0 ? Math.min(...finite) : r.threshold,
      appliesToFrontline: false,
      appliesToPeopleManagers: false,
      targetRoleIds: [],
    };
  });
  const rules = draftInputs(derived, catalog, loadedCodes);
  const modeOf = new Map(draft.map((r) => [r.code, r.mode]));
  let roles: RedFlagRoleRulesInput[] = live.map((c) => ({
    roleId: c.id,
    rules: rules
      .filter((r) => lines[c.id]?.[r.code]?.enabled === true)
      .map((r) => ({
        code: r.code,
        enabled: true,
        threshold: modeOf.get(r.code) === 'immediate' ? 1 : Math.trunc(Number(lines[c.id][r.code].threshold) || 0),
      })),
  }));
  const maxRoles = catalog?.limits?.maxRolesPerSet;
  if (maxRoles && roles.length > maxRoles) roles = roles.filter((r) => r.rules.length > 0);
  return { rules, roles };
};

/** The roles' limits the server enforces (catalog.limits), checked before a save. */
export const roleLimitErrors = (roles: RedFlagRoleRulesInput[], catalog: RedFlagRulesCatalog | null): string[] => {
  const limits = catalog?.limits;
  const errs: string[] = [];
  if (limits?.maxRolesPerSet && roles.length > limits.maxRolesPerSet) {
    errs.push(`Parameters can be on for at most ${limits.maxRolesPerSet} roles — switch some roles off.`);
  }
  const lineCount = roles.reduce((n, r) => n + r.rules.length, 0);
  if (limits?.maxRoleLines && lineCount > limits.maxRoleLines) {
    errs.push(`At most ${limits.maxRoleLines} role settings can be on at once — switch some off.`);
  }
  return errs;
};

const lineText = (line: RoleLine | undefined, mode: RedFlagTriggerMode): string => {
  if (!line?.enabled) return 'off';
  if (mode === 'immediate') return 'on';
  const n = Math.trunc(Number(line.threshold));
  const nth = Number.isFinite(n) ? ordinal(n) : 'Nth';
  return mode === 'every_nth' ? `every ${nth}` : `once at the ${nth}`;
};

/**
 * One line per role and parameter whose on/off or N changed, e.g.
 * "Team Leader · Late sign-in: every 5th → every 3rd" or "Promoter · Missed training: off → on".
 */
export const summariseRoleChanges = (
  baseline: RedFlagRuleView[],
  baselineLines: RoleRulesDraft,
  draft: RedFlagRuleView[],
  lines: RoleRulesDraft,
  cards: RoleCardInfo[],
): string[] => {
  const modeBefore = new Map(baseline.map((r) => [r.code, r.mode]));
  const out: string[] = [];
  for (const card of liveCards(cards)) {
    for (const r of draft) {
      const before = lineText(baselineLines[card.id]?.[r.code], modeBefore.get(r.code) ?? r.mode);
      const after = lineText(lines[card.id]?.[r.code], r.mode);
      if (before !== after) out.push(`${card.name} · ${r.label}: ${before} → ${after}`);
    }
  }
  return out;
};

/** A role name in the plural, for "Off for Promoters": "Team Leader" → "Team Leaders", "ASM" → "ASMs". */
export const rolePlural = (name: string): string => {
  const t = name.trim();
  if (!/[a-z]$/i.test(t) || /s$/i.test(t)) return t;
  const upper = t === t.toUpperCase();
  if (upper) return `${t}s`;
  if (/[^aeiou]y$/i.test(t)) return `${t.slice(0, -1)}ies`;
  if (/(x|z|ch|sh)$/i.test(t)) return `${t}es`;
  return `${t}s`;
};

/** The short line under a parameter for one role: "Every time", "Every 5th in a month", "Off for Promoters". */
export const roleLineText = (rule: RedFlagRuleView, line: RoleLine | undefined, card: RoleCardInfo): string => {
  if (!line?.enabled) {
    if (card.kind === 'none') return 'Off for people with no role';
    return card.kind === 'removed' ? 'Off' : `Off for ${rolePlural(card.name)}`;
  }
  const n = Math.trunc(Number(line.threshold));
  if (rule.mode === 'immediate' || (rule.mode === 'every_nth' && n === 1)) return 'Every time';
  const nth = Number.isFinite(n) && n >= 1 ? ordinal(n) : 'Nth';
  return rule.mode === 'every_nth' ? `Every ${nth} in a month` : `Once, at the ${nth} in a month`;
};
